// src/store/Sheets/sheet-contents/SearchSheetContent.tsx
import React, { useEffect, useState, useRef } from "react";
import { useSearchStore } from "../../../Search";
import { TEMPLATE_CODE } from "../../../Central/utils";
import { SheetContentProps } from "../../sheet-provider/sheetRegistry";
import { ClientResultCard } from "./components/results/ClientResultCard";
import { JobResultCard } from "./components/results/JobResultCard";
import { PatientResultCard } from "./components/results/PatientResultCard";
import { Button, CircularProgress } from "@mui/material";
import { SearchBar } from "./components/SearchBar";

const DEBOUNCE_MS = 250;

export const SearchSheetContent: React.FC<SheetContentProps> = ({
	sheetKey,
}) => {
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [showAllPatients, setShowAllPatients] = useState<boolean>(false);
	const [showAllJobs, setShowAllJobs] = useState<boolean>(false);
	const [showAllClients, setShowAllClients] = useState<boolean>(false);
	const [debouncing, setDebouncing] = useState(false);
	const debounceRef = useRef<NodeJS.Timeout | null>(null);
	const {
		clients,
		patients,
		jobs,
		loadingClients,
		loadingPatients,
		loadingJobs,
		fetchSearchResults,
		resetSearchResults,
	} = useSearchStore((state) => ({
		clients: state.searchResultsClients,
		patients: state.searchResultsPatients,
		jobs: state.searchResultsJobs,
		loadingClients: state.loadingClients,
		loadingPatients: state.loadingPatients,
		loadingJobs: state.loadingJobs,
		fetchSearchResults: state.fetchSearchResults,
		resetSearchResults: state.resetSearchResults,
	}));

	useEffect(() => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		if (searchQuery.length > 0) {
			setDebouncing(true);
			debounceRef.current = setTimeout(() => {
				setDebouncing(false);
				fetchSearchResults(searchQuery);
			}, DEBOUNCE_MS);
		} else {
			setDebouncing(false);
			resetSearchResults();
		}

		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [searchQuery]);

	useEffect(() => {
		resetSearchResults();
	}, []);

	return (
		<div className="flex flex-col gap-4">
			<SearchBar
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
			/>
			<div className="flex flex-col gap-4 p-4 mt-6">
				<div>
					<div className="flex flex-row items-center gap-2">
						<div className="text-sm font-medium">Kunden</div>
						{loadingClients && <CircularProgress size={20} />}
					</div>
					{clients
						?.filter((client) => client.code !== TEMPLATE_CODE)
						.slice(0, showAllClients ? clients.length : 5)
						.map((client) => {
							return (
								<ClientResultCard
									key={client.id}
									client={client}
								/>
							);
						})}
					{!showAllClients && clients.length > 5 && (
						<Button
							variant="text"
							onClick={() => setShowAllClients(true)}
						>
							Alle anzeigen
						</Button>
					)}

					{!loadingClients &&
						!debouncing &&
						clients.length === 0 &&
						(searchQuery.length > 0 ? (
							<div className="text-sm">Keine Kunden gefunden</div>
						) : (
							<div className="text-sm">
								Suchbegriffe: Code, Name
							</div>
						))}
				</div>
				<div>
					<div className="flex flex-row items-center gap-2">
						<div className="text-sm font-medium">Patienten</div>
						{loadingPatients && <CircularProgress size={20} />}
					</div>
					{patients
						?.slice(0, showAllPatients ? patients.length : 5)
						.map((patient) => {
							return (
								<PatientResultCard
									key={patient.patient_id}
									lazyPatient={patient}
								/>
							);
						})}
					{!showAllPatients && patients.length > 5 && (
						<Button
							variant="text"
							onClick={() => setShowAllPatients(true)}
						>
							Alle anzeigen
						</Button>
					)}
					{!loadingPatients &&
						!debouncing &&
						patients.length === 0 &&
						(searchQuery.length > 0 ? (
							<div className="text-sm">
								Keine Patienten gefunden
							</div>
						) : (
							<div className="text-sm">
								Suchbegriffe: Code, Name
							</div>
						))}
				</div>
				<div>
					<div className="flex flex-row items-center gap-2">
						<div className="text-sm font-medium">Aufträge</div>
						{loadingJobs && <CircularProgress size={20} />}
					</div>
					{jobs
						?.filter((job) => job.code !== TEMPLATE_CODE)
						.slice(0, showAllJobs ? jobs.length : 5)
						.map((job) => {
							return <JobResultCard key={job.id} job={job} />;
						})}
					{!showAllJobs && jobs.length > 5 && (
						<Button
							variant="text"
							onClick={() => setShowAllJobs(true)}
						>
							Alle anzeigen
						</Button>
					)}
					{!loadingJobs &&
						!debouncing &&
						jobs.length === 0 &&
						(searchQuery.length > 0 ? (
							<div className="text-sm">
								Keine Aufträge gefunden
							</div>
						) : (
							<div className="text-sm">
								Suchbegriffe: Code, Titel
							</div>
						))}
				</div>
			</div>
		</div>
	);
};
